import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
	Autocomplete,
	Box,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import {
	ALL_PRODUCT_SIZE_ID,
	ALL_PRODUCT_SIZE_OPTION,
	CREW_OPTIONS,
	type ProductSizeOption,
	SHIFT_OPTIONS,
} from "../models/StraightenerKpis";

interface Props {
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	productSizeId: number | null;
	onProductSizeChange: (id: number | null) => void;
	productSizes: ProductSizeOption[];
	productionShift: string | null;
	onProductionShiftChange: (shift: string | null) => void;
	crewId: string | null;
	onCrewChange: (crew: string | null) => void;
	onReset: () => void;
	onExportExcel: () => void;
	isLoading: boolean;
}

export const StraightenerKpisToolbar = ({
	start,
	end,
	onStartChange,
	onEndChange,
	productSizeId,
	onProductSizeChange,
	productSizes,
	productionShift,
	onProductionShiftChange,
	crewId,
	onCrewChange,
	onReset,
	onExportExcel,
	isLoading,
}: Props) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			px: 1.5,
			py: 0.75,
			flexWrap: "wrap",
		}}
	>
		<Box sx={{ minWidth: 150, maxWidth: 170 }}>
			<DatePicker
				label="Start"
				format="MM/DD/YYYY"
				value={start}
				maxDate={end}
				onChange={onStartChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>
		<Box sx={{ minWidth: 150, maxWidth: 170 }}>
			<DatePicker
				label="End"
				format="MM/DD/YYYY"
				value={end}
				minDate={start}
				onChange={onEndChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>

		{/* 508 sizes behind All and None, so the combo has to be searchable. It is not
		    clearable: the legacy control is a select that always carries a value, and
		    All is how you get back to no filter. */}
		<Box sx={{ width: 230 }}>
			<Autocomplete
				size="small"
				disableClearable
				options={productSizes}
				value={
					productSizes.find(
						(s) => s.Id === (productSizeId ?? ALL_PRODUCT_SIZE_ID),
					) ?? ALL_PRODUCT_SIZE_OPTION
				}
				getOptionLabel={(option) => option.Name}
				isOptionEqualToValue={(option, selected) => option.Id === selected.Id}
				onChange={(_event, option) =>
					onProductSizeChange(
						option.Id === ALL_PRODUCT_SIZE_ID ? null : option.Id,
					)
				}
				autoHighlight
				renderOption={(props, option) => {
					const { key, ...rest } = props as typeof props & { key: string };
					return (
						<li key={option.Id} {...rest}>
							{option.Name}
						</li>
					);
				}}
				renderInput={(params) => <TextField {...params} label="Product Size" />}
			/>
		</Box>

		<Box sx={{ width: 120 }}>
			<TextField
				select
				size="small"
				fullWidth
				label="Shift"
				value={productionShift ?? ""}
				onChange={(event) =>
					onProductionShiftChange(event.target.value || null)
				}
			>
				<MenuItem value="">All</MenuItem>
				{SHIFT_OPTIONS.map((shift) => (
					<MenuItem key={shift} value={shift}>
						{shift}
					</MenuItem>
				))}
			</TextField>
		</Box>

		<Box sx={{ width: 120 }}>
			<TextField
				select
				size="small"
				fullWidth
				label="Crew"
				value={crewId ?? ""}
				onChange={(event) => onCrewChange(event.target.value || null)}
			>
				<MenuItem value="">All</MenuItem>
				{CREW_OPTIONS.map((crew) => (
					<MenuItem key={crew} value={crew}>
						{crew}
					</MenuItem>
				))}
			</TextField>
		</Box>

		{/* Every filter refetches on change, so there is nothing for a search button
		    to do. This puts the screen back to the range and the filters it opens on. */}
		<Tooltip title="Reset the filters and reload">
			<span>
				<IconButton
					onClick={onReset}
					disabled={isLoading}
					size="small"
					sx={{
						bgcolor: "primary.main",
						color: "white",
						width: 28,
						height: 28,
						"&:hover": { bgcolor: "primary.dark" },
						"&.Mui-disabled": {
							bgcolor: "action.disabledBackground",
							color: "action.disabled",
						},
					}}
				>
					<RestartAltIcon sx={{ fontSize: 18 }} />
				</IconButton>
			</span>
		</Tooltip>

		<Box sx={{ flexGrow: 1, minWidth: 8 }} />

		<Box sx={{ width: 200 }}>
			<ExcelButton handleExportToExcel={onExportExcel} />
		</Box>
	</Box>
);
